import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

import {
  PlanningEntitySelectionChips,
  SelectionChip,
  SelectionChipIcon
} from 'components/shared/PlanningEntityPicker';

import { useActivityFilterLabels } from '../../hooks/useActivityFilterLabels';
import { AppliedFiltersRow } from './AppointmentsOverviewAppliedFilters.styles';

import type { AppointmentsOverviewAppliedFiltersProps } from './AppointmentsOverviewAppliedFilters.types';

/**
 * Every picked filter in one row of chips: the picked people and plan units, then the picked activities,
 * each with ✕, and Alles wissen. Nothing when nothing is picked. In the filter panel it lists the draft.
 */
const AppointmentsOverviewAppliedFilters = ({
  selections,
  onSelectionsChange,
  activityIds,
  onActivityIdsChange,
  showClearAll = true
}: AppointmentsOverviewAppliedFiltersProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const pickedActivities = useActivityFilterLabels(activityIds);

  if (selections.length === 0 && activityIds.length === 0) return null;

  const handleClearAll = () => {
    onSelectionsChange([]);
    onActivityIdsChange([]);
  };

  return (
    <AppliedFiltersRow>
      <PlanningEntitySelectionChips
        value={selections}
        onChange={onSelectionsChange}
      />
      {pickedActivities.map(({ entityId, label }) => (
        <SelectionChip
          key={entityId}
          icon={
            <SelectionChipIcon compact>
              <Icon type="clipboard-list" fontSize="small" />
            </SelectionChipIcon>
          }
          label={label}
          removeLabel={t('planningEntityPicker.removeLabel', { name: label })}
          onRemove={() =>
            onActivityIdsChange(activityIds.filter((id) => id !== entityId))
          }
        />
      ))}
      {showClearAll && (
        <Button variant="text" size="small" onClick={handleClearAll}>
          {t('appointmentsOverview.filters.clearAll')}
        </Button>
      )}
    </AppliedFiltersRow>
  );
};

export default AppointmentsOverviewAppliedFilters;
